//Loa đèn cảnh báo
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box, Card, Typography, CircularProgress } from '@mui/material';
import { sensorMapped } from '../../utils/sensorMapping';
import DefaultNode from '../../assets/sensor/default-node.svg';
import tempIcon from '../../assets/icons/temp-icon.svg';
import humIcon from '../../assets/icons/hum-icon.svg';
import { ControlLocationNodeType, useUpdateZoneControlMutation, useUpdateNodeATMControlMutation, ControlLocationGatewayType } from '../../services/control.service';
import { INodeType } from '../../services/node.service';
import { getMinutesDiffNow } from '../../utils/UtilsFunctions';
import { Switch } from 'common/Switch/Switch';
import Zone from '../../assets/sensor/zone.svg';
import { useSnackbar } from 'hooks/useSnackbar';
import { useAuth } from 'hooks/useAuth';

const DLNodeCard = styled(Card)({
  cursor: 'pointer',
  backgroundColor: '#F6F9FC',
  boxShadow: 'none',
  border: '1.5px solid #EEF2FA',
  borderRadius: '8px',
  padding: '16px 16px 8px',
  position:'relative'
});

const LoadingCard = styled(Card)({
  cursor: 'pointer',
  backgroundColor: '#d1d1d17c',
  boxShadow: 'none',
  borderRadius: '8px',
  position:'absolute',
  top:0,
  left:0,
  width:'100%',
  height:'100%',
  zIndex:1,
  display:"flex", 
  justifyContent:"center", 
  alignItems:"center",
});

const NodeBatteryBarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'percentage',
})<{ percentage: number }>(({ percentage }) => ({
  width: '20px',
  height: '10px',
  border: `1.5px solid ${percentage > 20 ? '#6fcf97' : '#FAD4A2'}`,
  borderRadius: '2.5px',
  position: 'relative',
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  padding: '1px',
  boxSizing: 'border-box',
  marginLeft: '4px',
}));

const NodeBatteryBarFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'percentage',
})<{ percentage: number }>(({ percentage }) => ({
  width: `${percentage}%`,
  height: '100%',
  borderRadius: '1.5px',
  backgroundColor: percentage > 20 ? '#6fcf97' : '#F2994A',
  transition: 'width 0.3s ease-in-out',
}));

const NodeBatteryTip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'percentage',
})<{ percentage: number }>(({ percentage }) => ({
  position: 'absolute',
  right: '-3px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '1.5px',
  height: '4px',
  backgroundColor: percentage > 20 ? '#6fcf97' : '#FAD4A2',
  borderRadius: '1px',
}));

type Props = {
  data: ControlLocationNodeType;
  nodeTypes: INodeType[];
  onClickCard: () => void;
  refetchGateway: () => void;
  showPin?: boolean;
  showStatus?: boolean;
  gatewayId?: string;
  locationId?: string;
  gatewayMode: number;
  gatewayConnection: string;
};

export const NodeCard = ({ data, nodeTypes, onClickCard, showPin = true, showStatus = true, gatewayId, locationId, refetchGateway, gatewayMode, gatewayConnection }: Props) => {
  const [updateNodeATMControl] = useUpdateNodeATMControlMutation();
  const nodeType = nodeTypes.find((item) => item.id === data.node_type_id);
  const nodeCodeType = nodeType?.code.split('-')[1];
  const {
    auth: { currentUser },
  } = useAuth();

  let isNodeATM =  Boolean(data.state?.sensor_type);

  const status = data.state && getMinutesDiffNow(data.state?.timestamp) < 130;
  const hum = data?.state?.hum;
  const temp = data?.state?.temp;
  let humValue, tempValue
  if(hum !== undefined) humValue = hum/100;
  if(temp !== undefined) tempValue = temp/100;

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { setSnackbar } = useSnackbar();

  const handleControlNodeATM = (event: React.ChangeEvent<HTMLInputElement>, data: any) => {
    if (currentUser && locationId && gatewayId && gatewayMode === 1 && gatewayConnection === 'online') {
      setIsLoading(true)
      updateNodeATMControl({
        agencyId: currentUser.sub_id,
        locationId: locationId,
        gatewayId: gatewayId,
        data: {
          sensor_type: data.state.sensor_type,
          alert_mode: event.target.checked
        },
      }).then((res:any) => {
        if(res && res.data?.success === true){
          setTimeout(()=>{
            setIsLoading(false)
            refetchGateway();
            setSnackbar({ open: true, message: 'Cập nhật chế độ cảnh báo thành công', severity: 'success' });
          },2000)
        }
      });
    }
  };

  return (
    <DLNodeCard onClick={onClickCard}>
      {isLoading &&
      <LoadingCard>
        <Box>
          <CircularProgress />
        </Box>
      </LoadingCard>
      }
      <Box display="flex" justifyContent={isNodeATM ? 'center' : 'space-between'} alignItems="center" pb={2}>
        {!isNodeATM && showStatus && (
          <Box>
            {status ? <img src={sensorMapped.power_on_dot} alt="" /> : <img src={sensorMapped.power_off_dot} alt="" />}
          </Box>
        )}

        {showPin ? (
          <Box display="flex" alignItems="center">
            <Typography style={{ fontSize: '12px', color: '#8B8C9B' }}>{data.state?.battery || 0}%</Typography>
            <NodeBatteryBarContainer percentage={data.state?.battery || 0}>
              <NodeBatteryBarFill percentage={data.state?.battery || 0} />
              <NodeBatteryTip percentage={data.state?.battery || 0} />
            </NodeBatteryBarContainer>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center"></Box>
        )}

        {isNodeATM && (
          <Box 
            onClick = {(event:React.MouseEvent<HTMLElement>) => event.stopPropagation()} 
            display="flex" justifyContent="center" alignItems="center"
          >
          <Switch 
            disabled={isLoading}
            checked={Boolean(data.state?.mode_alert)}
            onChange={(event) =>
              handleControlNodeATM(event, data)
            }
            />
          </Box>
        )}
      </Box>
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
        <Box style={{ minHeight: 78 }}>
          {nodeCodeType && (sensorMapped as any)[nodeCodeType] ? (
            <>
              {nodeCodeType === 'THB' ? (
                  <Box display={'flex'} justifyContent={'space-between'} gap={2} >
                    <Box pt={1} display={'flex'} justifyContent={'space-between'} flexDirection={'column'} alignItems={'center'}>
                      <img src={`${tempIcon}`} alt="" />
                      <Typography style={{ fontWeight: 500, lineHeight: '22px', textAlign: 'center', fontSize: '12px' }}>
                        {tempValue || '--'}ºC
                      </Typography>
                    </Box>
                    <Box>
                      <img src={(sensorMapped as any)[nodeCodeType]} alt="" />
                    </Box>
                    <Box pt={1} display={'flex'} justifyContent={'space-between'} flexDirection={'column'} alignItems={'center'}>
                      <img src={`${humIcon}`} alt="" />
                      <Typography style={{ fontWeight: 500, lineHeight: '22px', textAlign: 'center', fontSize: '12px'}}>
                        {humValue || '--'}%
                      </Typography>
                    </Box>
                  </Box>
              ): (
                <img src={(sensorMapped as any)[nodeCodeType]} alt="" />
              )}
            </>
          ) : (
            <img src={DefaultNode} alt="" />
          )}
        </Box>
        <Box mt={1} minHeight={'48px'}>
          <Typography style={{ fontWeight: 500, lineHeight: '22px', textAlign: 'center' }}>{data.name}</Typography>
        </Box>
      </Box>
    </DLNodeCard>
  );
};

export const ZoneCard = ({ item, onClickCard, locationId, gatewayId, onRefreshZone }: { item: ControlLocationNodeType; onClickCard: () => void; locationId: string; gatewayId: string, onRefreshZone: () => void }) => {
  const [updateZoneControl, { isLoading: isLoadingUpdate }] = useUpdateZoneControlMutation();
  const {
    auth: { currentUser },
  } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { setSnackbar } = useSnackbar();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, zoneId: string) => {
    if (currentUser && locationId && gatewayId) {
      setIsLoading(true)
      updateZoneControl({
        agencyId: currentUser.sub_id,
        locationId: locationId,
        gatewayId: gatewayId,
        nodeId: zoneId,
        data: {'power':event.target.checked},
      }).then((res:any) => {
        if(res && res.data?.success === true){
          setTimeout(()=>{
            setIsLoading(false)
            onRefreshZone();
            setSnackbar({ open: true, message: 'Cập nhật zone thành công', severity: 'success' });
          },2000)
        }
      });
    }
  };
 
  return (
    <DLNodeCard onClick={onClickCard} >
      {isLoading &&
      <LoadingCard>
        <Box>
          <CircularProgress />
        </Box>
      </LoadingCard>
      }
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" justifyContent="center" >
          <Box display="flex" alignItems="center" justifyContent="center">
            <img src={Zone} alt="" />
          </Box>
          <Box ml={2}>
            <Typography style={{ fontWeight: 500, lineHeight: '22px', textAlign: 'center' }}>{item.name}</Typography>
          </Box>
        </Box>
        <Box onClick = {(event:React.MouseEvent<HTMLElement>) => event.stopPropagation()} >
          <Switch 
            disabled={isLoading}
            checked={Boolean(item.state?.power)}
            onChange={(event) =>
              handleChange(event, item.id)
            }
            />
        </Box>
      </Box>
    </DLNodeCard>
  );
};
